import React, { useCallback, useEffect, useState } from 'react';

import {
  Container,
  BotaoExcluir,
  BotaoSalvar,
  BotoesContainer,
  InputObs,
  InputData,
} from './styles';

import { FiSave, FiTrash2 } from 'react-icons/fi';

import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useConfigs } from '../../context/ConfigsContext';
import { returnAxiosError } from '../../utils/returnAxiosError';

import ComboboxComFiltro from '../../components/autocomplete';

import ICategoria from '../../routes/vms/ICategoria';
import IProfissional from '../../routes/vms/IProfissional';
import IAgendamento from '../../routes/vms/IAgendamento';
import { adicionarMinutos } from '../../utils/adicionarMinutos';

interface HorariosCombo {
  id: string;
  nome: string;
}

interface BloqueioProps {
  onClose: () => void;
  bloqueioParametro: IAgendamento | undefined;
  profissionalParametro: string;
  dataParametro: Date;
  horaParametro: string;
}

const Bloqueio: React.FC<BloqueioProps> = ({
  bloqueioParametro,
  profissionalParametro,
  dataParametro,
  horaParametro,
  onClose,
}) => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const { lerConfig } = useConfigs();

  const [data, setData] = useState<string>('');

  const [profissional, setProfissional] = useState<string | null>(
    profissionalParametro,
  );
  const [profissionalErro, setProfissionalErro] = useState<string>('');
  const [profissionais, setProfissionais] = useState<IProfissional[]>([]);

  const [categoria, setCategoria] = useState<string | null>(null);
  const [categoriaErro, setCategoriaErro] = useState<string>('');
  const [categorias, setCategorias] = useState<ICategoria[]>([]);

  const [horaIni, setHoraIni] = useState<string | null>(null);
  const [horaIniErro, setHoraIniErro] = useState<string>('');

  const [obs, setObs] = useState<string>('');

  const [horaFim, setHoraFim] = useState<string | null>(null);
  const [horaFimErro, setHoraFimErro] = useState<string>('');

  const [horarios, setHorarios] = useState<HorariosCombo[]>([]);
  const [intervalo, setIntervalo] = useState<string>('');

  const atualizaDados = useCallback(async () => {
    try {
      const [categoriasResponse, profissionaisResponse] = await Promise.all([
        api.get<ICategoria[]>('/categoria/lista', {
          params: { inutilizar: true },
        }),
        api.get<IProfissional[]>(
          `/users/profissionais?empresa_id=${user.empresa_id}`,
        ),
      ]);

      setCategorias(categoriasResponse.data);
      setProfissionais(profissionaisResponse.data);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
    }
  }, [user.empresa_id]);

  const aoAlterarProfissional = (novoProfissional: string | null) => {
    setProfissional(novoProfissional);
    setProfissionalErro('');
  };

  const aoAlterarData = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setData(event.target.value);
    },
    [],
  );

  const aoAlterarCategoria = useCallback(
    async (novaCategoria: string | null) => {
      setCategoria(novaCategoria);
      setCategoriaErro('');

      const horariosArray = await api.get<{
        horarios: string[];
        metade_intervalo: boolean;
      }>(`/empresas/horarios/`, {
        params: {
          url_frontend: user.empresa.url_frontend,
          categoria_id: novaCategoria,
        },
      });

      const horariosCombo: HorariosCombo[] = horariosArray.data.horarios.map(
        horario => ({
          id: horario,
          nome: horario,
        }),
      );

      setHorarios(horariosCombo);

      setHoraFim(
        adicionarMinutos(
          horaIni,
          horariosArray.data.metade_intervalo
            ? String(Number(intervalo) / 2)
            : intervalo,
        ),
      );
    },
    [user.empresa.url_frontend, horaIni, intervalo],
  );

  const aoAlterarObs = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setObs(event.target.value);
    },
    [],
  );

  const aoAlterarHoraIni = useCallback(
    async (novoHorario: string | null) => {
      setHoraIni(novoHorario);

      try {
        const metade_intervalo = await api.get(
          `/categoria/${categoria ? categoria : ''}`,
        );

        setHoraFim(
          adicionarMinutos(
            novoHorario,
            metade_intervalo.data ? String(Number(intervalo) / 2) : intervalo,
          ),
        );
      } catch (error) {
        setHoraFim(adicionarMinutos(novoHorario, intervalo));
      }

      setHoraIniErro('');
    },
    [categoria, intervalo],
  );

  const aoAlterarHoraFim = (novoHorario: string | null) => {
    setHoraFim(novoHorario);
    setHoraFimErro('');
  };

  const validacoesAgendamento = useCallback(() => {
    setProfissionalErro('');
    setHoraIniErro('');
    setHoraFimErro('');
    setCategoriaErro('');

    let erroValidacao = false;
    if (!profissional) {
      setProfissionalErro('Informação obrigatória!');
      erroValidacao = true;
    }

    if (!horaIni) {
      setHoraIniErro('Informação obrigatória!');
      erroValidacao = true;
    }

    if (!horaFim) {
      setHoraFimErro('Informação Obrigatória!');
      erroValidacao = true;
    }

    if (!categoria) {
      setCategoriaErro('Informação obrigatória!');
      erroValidacao = true;
    }

    if (erroValidacao) {
      addToast({
        type: 'info',
        title: 'Pendências na validação',
        description:
          'Efetue as correções indicadas em cada campo e tente novamente.',
      });
      return false;
    }
  }, [addToast, profissional, horaIni, horaFim, categoria]);

  const defineHorarios = useCallback(async () => {
    const horariosArray = await api.get<{ horarios: string[] }>(
      `/empresas/horarios/`,
      {
        params: {
          url_frontend: user.empresa.url_frontend,
        },
      },
    );

    const horariosCombo: HorariosCombo[] = horariosArray.data.horarios.map(
      horario => ({
        id: horario,
        nome: horario,
      }),
    );

    setHorarios(horariosCombo);
  }, [user]);

  const handleExcluirAgendamento = useCallback(async () => {
    if (!bloqueioParametro) {
      addToast({
        type: 'error',
        title: 'Erro ao excluir',
        description: 'Nenhum horário inativo selecionado para exclusão',
      });

      return;
    }

    try {
      await api.delete(`/agendamentos/inutilizado/`, {
        params: { agendamento_id: bloqueioParametro.id },
      });
    } catch (error) {
      addToast(returnAxiosError(error));

      return;
    }

    addToast({
      type: 'success',
      title: 'Removido',
      description: 'Horári inutilizado excluido',
    });

    onClose();
  }, [addToast, bloqueioParametro, onClose]);

  const aoSalvar = useCallback(async () => {
    if (validacoesAgendamento() === false) {
      return;
    }

    let ano;
    let mes;
    let dia;

    let dataFormatada;

    ano = dataParametro.getFullYear();
    mes = String(dataParametro.getMonth() + 1).padStart(2, '0'); // getMonth() retorna de 0 a 11
    dia = String(dataParametro.getDate()).padStart(2, '0');
    dataFormatada = `${ano}-${mes}-${dia}`;

    try {
      if (bloqueioParametro) {
        await api.put(`/agendamentos/inutilizar/${bloqueioParametro.id}`, {
          categoria_id: categoria,
          hora: horaIni,
          observacoes: obs.toUpperCase(),
          data_criacao: data,
          profissional_id: profissional ? profissional : '',
        });
      } else {
        await api.post('/agendamentos/inutilizar', {
          horaIni,
          horaFim,
          categoria_id: categoria,
          profissional_id: profissional ? profissional : '',
          data_criacao: dataFormatada ?? '',
          observacoes: obs.toUpperCase(),
        });
      }
    } catch (error) {
      addToast(returnAxiosError(error));

      return;
    }

    addToast({
      type: 'success',
      title: 'Parabéns',
      description: bloqueioParametro
        ? 'Horário inutilizado alterado com sucesso'
        : 'Horário inutilizado com sucesso',
    });

    onClose();
  }, [
    addToast,
    bloqueioParametro,
    categoria,
    data,
    dataParametro,
    obs,
    horaIni,
    horaFim,
    profissional,
    validacoesAgendamento,
    onClose,
  ]);

  useEffect(() => {
    atualizaDados();
    defineHorarios();
    setIntervalo(lerConfig('INTERVALO'));
  }, [atualizaDados, defineHorarios, lerConfig]);

  useEffect(() => {
    if (bloqueioParametro) {
      setObs(bloqueioParametro.observacoes);
      setData(bloqueioParametro.data_criacao.toString().split('T')[0]);

      const categoriaId = bloqueioParametro.categoria
        ? bloqueioParametro.categoria.id
        : '';

      setCategoria(categoriaId);

      api
        .get<{ horarios: string[]; metade_intervalo: boolean }>(
          `/empresas/horarios/`,
          {
            params: {
              url_frontend: user.empresa.url_frontend,
              categoria_id: categoriaId,
            },
          },
        )
        .then(horariosArray => {
          const horariosCombo: HorariosCombo[] =
            horariosArray.data.horarios.map(horario => ({
              id: horario,
              nome: horario,
            }));

          setHorarios(horariosCombo);

          setHoraIni(horaParametro);
          setHoraFim(
            adicionarMinutos(
              horaParametro,
              horariosArray.data.metade_intervalo
                ? String(Number(intervalo) / 2)
                : intervalo,
            ),
          );
        });
    }
  }, [bloqueioParametro, horaParametro, intervalo, user.empresa.url_frontend]);

  useEffect(() => {
    setHoraIni(horaParametro);
    setHoraFim(adicionarMinutos(horaParametro, intervalo));
  }, [horaParametro, intervalo]);

  return (
    <Container>
      <h1>Bloqueio</h1>

      <span>
        {bloqueioParametro && (
          <InputData
            variant="outlined"
            margin="normal"
            id="data"
            label="Data"
            name="data"
            type="date"
            InputLabelProps={{
              shrink: true,
            }}
            value={data}
            onChange={aoAlterarData}
            style={{ marginRight: '8px', flex: 2 }}
          />
        )}

        <ComboboxComFiltro
          opcoes={profissionais}
          value={profissional}
          label="Profissional"
          onChange={aoAlterarProfissional}
          error={profissionalErro !== ''}
          helperText={profissionalErro}
          size={5}
          temMargin={true}
        />

        <ComboboxComFiltro
          opcoes={categorias}
          value={categoria}
          label="Categoria"
          onChange={aoAlterarCategoria}
          error={categoriaErro !== ''}
          helperText={categoriaErro}
          size={3}
          temMargin={true}
        />

        <ComboboxComFiltro
          opcoes={horarios}
          value={horaIni}
          label="Hora Início"
          onChange={aoAlterarHoraIni}
          error={horaIniErro !== ''}
          helperText={horaIniErro}
          size={1.4}
          temMargin={true}
        />

        <ComboboxComFiltro
          opcoes={horarios}
          value={horaFim}
          label="Hora Fim"
          onChange={aoAlterarHoraFim}
          error={horaFimErro !== ''}
          helperText={horaFimErro}
          size={1.4}
        />
      </span>

      <InputObs
        variant="outlined"
        margin="normal"
        id="obs"
        label="Observações"
        name="obs"
        value={obs}
        onChange={aoAlterarObs}
      />

      <BotoesContainer>
        <BotaoSalvar onClick={aoSalvar}>
          <FiSave />
          Salvar
        </BotaoSalvar>

        <BotaoExcluir onClick={() => handleExcluirAgendamento()}>
          <FiTrash2 />
          Excluir
        </BotaoExcluir>
      </BotoesContainer>
    </Container>
  );
};

export default Bloqueio;
